import { Component, EventEmitter, Input, Output, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProjectService, Project } from '../../../../../services/project.service';
import { WebsiteService } from '../../../../../services/website.service';
import { Subject } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';

@Component({
  selector: 'app-project-selector-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './project-selector-modal.component.html',
  styleUrls: ['./project-selector-modal.component.scss']
})
export class ProjectSelectorModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() show = false;
  @Input() selectedProjectIds: string[] = [];
  @Input() currentLocale: string = 'nl';
  @Output() onSelect = new EventEmitter<string[]>();
  @Output() onClose = new EventEmitter<void>();

  projects: Project[] = [];
  filteredProjects: Project[] = [];
  loading = false;
  searchQuery: string = '';
  selectedProjects: Set<string> = new Set();

  private destroy$ = new Subject<void>();

  constructor(
    private projectService: ProjectService,
    private websiteService: WebsiteService
  ) {}

  ngOnInit() {
    if (this.show) {
      this.loadProjects();
      this.initializeSelection();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['show'] && changes['show'].currentValue && !changes['show'].previousValue) {
      this.loadProjects();
      this.initializeSelection();
    }
    if (changes['selectedProjectIds']) {
      this.initializeSelection();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initializeSelection() {
    this.selectedProjects = new Set(this.selectedProjectIds || []);
  }

  loadProjects() {
    this.loading = true;
    this.projectService.getProjects()
      .pipe(
        takeUntil(this.destroy$),
        map(projects => {
          const currentDomain = this.websiteService.getCurrentDomain();
          if (currentDomain) {
            return projects.filter(p => {
              const domains = p['domains'] || [];
              return domains.includes(currentDomain);
            });
          }
          return projects;
        })
      )
      .subscribe({
        next: (projects) => {
          this.projects = projects;
          this.filterProjects();
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading projects:', error);
          this.loading = false;
        }
      });
  }

  filterProjects() {
    if (!this.searchQuery.trim()) {
      this.filteredProjects = this.projects;
      return;
    }

    const query = this.searchQuery.toLowerCase().trim();
    this.filteredProjects = this.projects.filter(p => {
      const title = this.getProjectTitle(p).toLowerCase();
      const description = this.getProjectDescription(p).toLowerCase();
      const categories = (p.categories || []).join(' ').toLowerCase();
      const tags = (p.tags || []).join(' ').toLowerCase();
      
      return title.includes(query) || 
             description.includes(query) || 
             categories.includes(query) ||
             tags.includes(query);
    });
  }

  onSearchChange() {
    this.filterProjects();
  }

  toggleProject(projectId: string) {
    if (this.selectedProjects.has(projectId)) {
      this.selectedProjects.delete(projectId);
    } else {
      this.selectedProjects.add(projectId);
    }
  }

  isSelected(projectId: string): boolean {
    return this.selectedProjects.has(projectId);
  }

  confirmSelection() {
    const selectedIds = Array.from(this.selectedProjects);
    this.onSelect.emit(selectedIds);
    this.onClose.emit();
  }

  close() {
    this.onClose.emit();
  }

  getProjectTitle(project: Project): string {
    const locale = this.currentLocale;
    return project[`title_${locale}`] || project.title_nl || project.title || 'Untitled Project';
  }

  getProjectDescription(project: Project): string {
    const locale = this.currentLocale;
    const desc = project[`description_${locale}`] || project.description_nl || project.description || '';
    return desc.replace(/<[^>]*>/g, '').substring(0, 100) + (desc.length > 100 ? '...' : '');
  }

  getProjectImage(project: Project): string {
    if (project.image) {
      return project.image;
    }
    if (project.gallery && project.gallery.length > 0) {
      return project.gallery[0].url;
    }
    return '';
  }

  getSelectedCount(): number {
    return this.selectedProjects.size;
  }
}
